import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Receipt, ChevronDown, ChevronRight, RefreshCw, Users, Layers,
  AlertTriangle, DollarSign, Mail, PhoneCall, FileText, ArrowLeft, ArrowRight,
  Building
} from 'lucide-react';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const TRAMOS = [
  { key: 'vigente', label: 'Vigente', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  { key: '0_30', label: '0 - 30 días', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  { key: '31_60', label: '31 - 60 días', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  { key: '61_90', label: '61 - 90 días', color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  { key: 'mas_90', label: 'Más de 90 días', color: 'text-red-700', bg: 'bg-red-100 border-red-300' },
];

const fmtCLP = (n) => new Intl.NumberFormat('es-CL').format(Math.round(Number(n || 0)));

/**
 * Componente principal de "En Cobro" con dos vistas: por cuenta/RUT y por tramos excluyentes.
 * Reemplaza la vista factura-a-factura anterior manteniendo el detalle individual dentro
 * de la expansión por cuenta y en la vista por tramos.
 */
const EnCobroConsolidado = ({
  onCrearGestion,
  onCrearCompromiso,
  onEnviarEstadoCuenta,
  onAbrirRegistrarPago,
  onAbrirNotaCredito,
  onAbrirNotaDebito,
}) => {
  const [vista, setVista] = useState('por_cuenta'); // 'por_cuenta' | 'por_tramos'
  const [orden, setOrden] = useState('mora_desc'); // 'mora_desc' | 'nombre_asc'
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  // Vista por cuenta
  const [dataCuentas, setDataCuentas] = useState({ total: 0, cuentas: [], totales_globales: {}, total_pages: 0 });
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [expandedRut, setExpandedRut] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Vista por tramos
  const [tramoActivo, setTramoActivo] = useState('mas_90');
  const [dataTramo, setDataTramo] = useState({ total: 0, facturas: [], resumen: {}, total_pages: 0 });
  const [loadingTramo, setLoadingTramo] = useState(false);

  // -------------------- fetchers --------------------
  const fetchCuentas = useCallback(async () => {
    setLoadingCuentas(true);
    try {
      const params = new URLSearchParams({
        orden,
        page: String(page),
        page_size: String(pageSize),
        solo_con_pendiente: 'true',
      });
      const r = await api.get(`/finanzas/cobranza/en-cobro/por-cuenta?${params.toString()}`);
      setDataCuentas(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al cargar cuentas consolidadas');
    } finally {
      setLoadingCuentas(false);
    }
  }, [orden, page, pageSize]);

  const fetchTramo = useCallback(async () => {
    setLoadingTramo(true);
    try {
      const params = new URLSearchParams({
        tramo: tramoActivo,
        page: String(page),
        page_size: String(pageSize),
      });
      const r = await api.get(`/finanzas/cobranza/en-cobro/por-tramos?${params.toString()}`);
      setDataTramo(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al cargar facturas por tramo');
    } finally {
      setLoadingTramo(false);
    }
  }, [tramoActivo, page, pageSize]);

  const fetchDetalle = useCallback(async (rut) => {
    setLoadingDetalle(true);
    setDetalle(null);
    try {
      const r = await api.get(`/finanzas/cobranza/cuentas-consolidadas/${encodeURIComponent(rut)}/detalle-completo`);
      setDetalle(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al cargar detalle de la cuenta');
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  useEffect(() => {
    if (vista === 'por_cuenta') fetchCuentas();
    else fetchTramo();
  }, [vista, fetchCuentas, fetchTramo]);

  // Cambia de vista y resetea paginación
  const handleChangeVista = (v) => {
    setVista(v);
    setPage(1);
    setExpandedRut(null);
    setDetalle(null);
  };

  const handleChangePageSize = (val) => {
    setPageSize(Number(val));
    setPage(1);
  };

  const handleToggleExpand = (rut) => {
    if (expandedRut === rut) {
      setExpandedRut(null);
      setDetalle(null);
    } else {
      setExpandedRut(rut);
      fetchDetalle(rut);
    }
  };

  const handleChangeTramo = (t) => {
    setTramoActivo(t);
    setPage(1);
  };

  const globalTotales = dataCuentas.totales_globales || {};
  const tramoResumen = dataTramo.resumen || {};

  return (
    <div className='space-y-4' data-testid='en-cobro-consolidado'>
      {/* Selector de vista + controles */}
      <div className='flex items-center gap-3 flex-wrap'>
        <div className='inline-flex bg-slate-100 rounded-lg p-1' role='tablist'>
          <button
            type='button'
            data-testid='vista-por-cuenta-btn'
            onClick={() => handleChangeVista('por_cuenta')}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
              vista === 'por_cuenta' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className='w-4 h-4' /> Por cliente / RUT
          </button>
          <button
            type='button'
            data-testid='vista-por-tramos-btn'
            onClick={() => handleChangeVista('por_tramos')}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
              vista === 'por_tramos' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers className='w-4 h-4' /> Por tramos excluyentes
          </button>
        </div>

        {vista === 'por_cuenta' && (
          <Select value={orden} onValueChange={(v) => { setOrden(v); setPage(1); }}>
            <SelectTrigger className='w-52 h-9 text-sm' data-testid='orden-select'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='mora_desc'>Mayor mora primero</SelectItem>
              <SelectItem value='nombre_asc'>Nombre A → Z</SelectItem>
            </SelectContent>
          </Select>
        )}

        <div className='flex items-center gap-1 text-xs text-slate-600'>
          <span>Filas:</span>
          <Select value={String(pageSize)} onValueChange={handleChangePageSize}>
            <SelectTrigger className='w-20 h-8 text-xs' data-testid='page-size-select'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(n => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          size='sm'
          variant='outline'
          className='h-9 text-xs ml-auto'
          onClick={() => (vista === 'por_cuenta' ? fetchCuentas() : fetchTramo())}
          data-testid='refresh-btn'
        >
          <RefreshCw className='w-3 h-3 mr-1' /> Actualizar
        </Button>
      </div>

      {vista === 'por_cuenta' ? (
        <VistaPorCuenta
          data={dataCuentas}
          loading={loadingCuentas}
          expandedRut={expandedRut}
          detalle={detalle}
          loadingDetalle={loadingDetalle}
          globalTotales={globalTotales}
          onToggleExpand={handleToggleExpand}
          onCrearGestion={onCrearGestion}
          onCrearCompromiso={onCrearCompromiso}
          onEnviarEstadoCuenta={onEnviarEstadoCuenta}
          onAbrirRegistrarPago={onAbrirRegistrarPago}
          onAbrirNotaCredito={onAbrirNotaCredito}
          onAbrirNotaDebito={onAbrirNotaDebito}
          page={page}
          setPage={setPage}
        />
      ) : (
        <VistaPorTramos
          data={dataTramo}
          loading={loadingTramo}
          tramoActivo={tramoActivo}
          tramoResumen={tramoResumen}
          onChangeTramo={handleChangeTramo}
          page={page}
          setPage={setPage}
          onAbrirRegistrarPago={onAbrirRegistrarPago}
          onAbrirNotaCredito={onAbrirNotaCredito}
          onAbrirNotaDebito={onAbrirNotaDebito}
        />
      )}
    </div>
  );
};

// ---------------------- Vista por cuenta ----------------------
const VistaPorCuenta = ({
  data, loading, expandedRut, detalle, loadingDetalle, globalTotales,
  onToggleExpand, onCrearGestion, onCrearCompromiso, onEnviarEstadoCuenta,
  onAbrirRegistrarPago, onAbrirNotaCredito, onAbrirNotaDebito,
  page, setPage,
}) => {
  const cuentas = data.cuentas || [];
  const totalPages = data.total_pages || 1;

  return (
    <>
      {/* Resumen global */}
      <Card className='p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'>
        <div className='flex gap-6 flex-wrap'>
          <div className='flex items-center gap-2'>
            <Users className='w-4 h-4 text-blue-600' />
            <span className='text-sm text-slate-600'>Cuentas: <strong className='text-blue-700'>{globalTotales.n_cuentas || 0}</strong></span>
          </div>
          <div className='flex items-center gap-2'>
            <DollarSign className='w-4 h-4 text-amber-600' />
            <span className='text-sm text-slate-600'>Saldo total: <strong className='text-amber-700'>${fmtCLP(globalTotales.saldo_total)}</strong></span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-3 h-3 rounded-full bg-blue-400' />
            <span className='text-sm text-slate-600'>Vigente: <strong className='text-blue-700'>${fmtCLP(globalTotales.saldo_vigente)}</strong></span>
          </div>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='w-4 h-4 text-red-500' />
            <span className='text-sm text-slate-600'>Vencido: <strong className='text-red-700'>${fmtCLP(globalTotales.saldo_vencido)}</strong></span>
          </div>
        </div>
      </Card>

      {/* Tabla de cuentas */}
      <Card className='border-slate-200'>
        <div className='overflow-x-auto'>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2' />
              <p className='text-slate-600'>Cargando cuentas...</p>
            </div>
          ) : cuentas.length === 0 ? (
            <div className='p-12 text-center'>
              <Receipt className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-slate-600 font-medium'>No hay cuentas con saldo pendiente</p>
            </div>
          ) : (
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 sticky top-0 z-10'>
                <tr>
                  <th className='p-2'></th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>Cliente</th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>RUT</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Saldo Total</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Vigente</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Vencido</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>0-30</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>31-60</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>61-90</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>+90</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>#Fact</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Días Mora</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((cuenta) => (
                  <React.Fragment key={cuenta.rut_normalizado}>
                    <tr
                      className='border-b border-slate-100 hover:bg-slate-50 cursor-pointer'
                      onClick={() => onToggleExpand(cuenta.rut_normalizado)}
                      data-testid={`cuenta-row-${cuenta.rut_normalizado}`}
                    >
                      <td className='p-2 text-center'>
                        {expandedRut === cuenta.rut_normalizado
                          ? <ChevronDown className='w-4 h-4 text-slate-500 inline' />
                          : <ChevronRight className='w-4 h-4 text-slate-500 inline' />}
                      </td>
                      <td className='p-3'>
                        <p className='font-medium text-slate-900'>{cuenta.nombre_principal}</p>
                        {cuenta.n_client_ids > 1 && (
                          <p className='text-xs text-slate-500'>{cuenta.client_ids?.length || cuenta.n_client_ids} registros vinculados</p>
                        )}
                      </td>
                      <td className='p-3 font-mono text-xs'>{cuenta.rut_display}</td>
                      <td className='p-3 text-right font-semibold text-slate-900'>${fmtCLP(cuenta.saldo_total)}</td>
                      <td className='p-3 text-right text-blue-700'>${fmtCLP(cuenta.saldo_vigente)}</td>
                      <td className='p-3 text-right text-red-700'>${fmtCLP(cuenta.saldo_vencido)}</td>
                      <td className='p-3 text-right text-amber-600'>${fmtCLP(cuenta.saldos_por_tramo?.['0_30'])}</td>
                      <td className='p-3 text-right text-orange-600'>${fmtCLP(cuenta.saldos_por_tramo?.['31_60'])}</td>
                      <td className='p-3 text-right text-red-500'>${fmtCLP(cuenta.saldos_por_tramo?.['61_90'])}</td>
                      <td className='p-3 text-right text-red-700 font-medium'>${fmtCLP(cuenta.saldos_por_tramo?.mas_90)}</td>
                      <td className='p-3 text-center'>{cuenta.n_facturas_pendientes}</td>
                      <td className='p-3 text-center'>
                        {cuenta.dias_mora_mas_antigua > 0
                          ? <Badge variant='outline' className='bg-red-50 text-red-700 border-red-200'>{cuenta.dias_mora_mas_antigua} días</Badge>
                          : <span className='text-slate-400 text-xs'>—</span>}
                      </td>
                      <td className='p-3 text-center' onClick={(e) => e.stopPropagation()}>
                        <div className='flex items-center gap-1 justify-center'>
                          <Button size='sm' variant='outline' className='h-7 text-[11px]'
                            onClick={() => onCrearGestion?.(cuenta)}
                            data-testid={`crear-gestion-${cuenta.rut_normalizado}`}>
                            <PhoneCall className='w-3 h-3 mr-1' /> Gestión
                          </Button>
                          <Button size='sm' variant='outline' className='h-7 text-[11px]'
                            onClick={() => onCrearCompromiso?.(cuenta)}
                            data-testid={`crear-compromiso-${cuenta.rut_normalizado}`}>
                            <FileText className='w-3 h-3 mr-1' /> Compromiso
                          </Button>
                          <Button size='sm' variant='outline' className='h-7 text-[11px]'
                            onClick={() => onEnviarEstadoCuenta?.(cuenta)}
                            data-testid={`enviar-estado-${cuenta.rut_normalizado}`}>
                            <Mail className='w-3 h-3 mr-1' /> Estado
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedRut === cuenta.rut_normalizado && (
                      <tr>
                        <td colSpan={13} className='bg-slate-50 p-4'>
                          <DetalleCuentaExpandido
                            loading={loadingDetalle}
                            detalle={detalle}
                            onAbrirRegistrarPago={onAbrirRegistrarPago}
                            onAbrirNotaCredito={onAbrirNotaCredito}
                            onAbrirNotaDebito={onAbrirNotaDebito}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Paginacion page={page} totalPages={totalPages} onChange={setPage} total={data.total} />
    </>
  );
};

// ---------------------- Detalle expandido ----------------------
const DetalleCuentaExpandido = ({ loading, detalle, onAbrirRegistrarPago, onAbrirNotaCredito, onAbrirNotaDebito }) => {
  const [seccion, setSeccion] = useState('facturas');

  if (loading || !detalle) {
    return (
      <div className='p-6 text-center text-slate-500 text-sm'>
        <div className='animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2' />
        Cargando detalle...
      </div>
    );
  }

  const SECCIONES = [
    { key: 'facturas', label: 'Facturas', count: detalle.facturas?.length || 0 },
    { key: 'pagos', label: 'Pagos', count: detalle.pagos?.length || 0 },
    { key: 'ncnd', label: 'NC/ND', count: (detalle.notas_credito?.length || 0) + (detalle.notas_debito?.length || 0) },
    { key: 'gestiones', label: 'Gestiones', count: detalle.gestiones?.length || 0 },
    { key: 'compromisos', label: 'Compromisos', count: detalle.compromisos?.length || 0 },
    { key: 'alertas', label: 'Alertas', count: detalle.alertas?.length || 0 },
    { key: 'comunicaciones', label: 'Comunicaciones', count: detalle.comunicaciones?.length || 0 },
    { key: 'contactos', label: 'Contactos', count: detalle.contactos?.length || 0 },
  ];

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-1 flex-wrap'>
        {SECCIONES.map(s => (
          <button
            key={s.key}
            type='button'
            onClick={() => setSeccion(s.key)}
            data-testid={`detalle-tab-${s.key}`}
            className={`px-3 py-1 text-xs rounded ${seccion === s.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
          >
            {s.label} <span className='opacity-70'>({s.count})</span>
          </button>
        ))}
      </div>

      <div className='bg-white rounded border border-slate-200 p-3 max-h-80 overflow-y-auto'>
        {seccion === 'facturas' && (
          <TablaSimple
            headers={['N° Factura', 'Emitida', 'Vencimiento', 'Total', 'Pendiente', 'Estado', 'Acciones']}
            rows={detalle.facturas?.map(f => [
              `#${f.numero_factura || f.id?.slice(0,8)}`,
              f.fecha_emision ? new Date(f.fecha_emision).toLocaleDateString('es-CL') : '—',
              f.fecha_vencimiento ? new Date(f.fecha_vencimiento).toLocaleDateString('es-CL') : '—',
              `$${fmtCLP(f.total_amount_clp || f.monto_clp)}`,
              `$${fmtCLP(f.monto_pendiente || f.pending_amount_clp || 0)}`,
              f.status,
              <div key='actions' className='flex gap-1'>
                <Button size='sm' variant='outline' className='h-6 text-[10px]' onClick={() => onAbrirRegistrarPago?.(f)}>Pago</Button>
                <Button size='sm' variant='outline' className='h-6 text-[10px]' onClick={() => onAbrirNotaCredito?.(f)}>NC</Button>
                <Button size='sm' variant='outline' className='h-6 text-[10px]' onClick={() => onAbrirNotaDebito?.(f)}>ND</Button>
              </div>,
            ])}
          />
        )}
        {seccion === 'pagos' && (
          <TablaSimple
            headers={['Fecha', 'Monto', 'Método', 'Referencia']}
            rows={detalle.pagos?.map(p => [
              p.fecha_pago ? new Date(p.fecha_pago).toLocaleDateString('es-CL') : '—',
              `$${fmtCLP(p.amount_clp || p.monto_clp || p.monto)}`,
              p.metodo_pago || p.metodo || '—',
              p.referencia || p.numero_transaccion || '—',
            ])}
          />
        )}
        {seccion === 'ncnd' && (
          <div className='space-y-4'>
            <div>
              <p className='text-xs font-semibold text-blue-700 mb-1'>Notas de Crédito ({detalle.notas_credito?.length || 0})</p>
              <TablaSimple
                headers={['N°', 'Fecha', 'Monto', 'Motivo']}
                rows={detalle.notas_credito?.map(n => [
                  n.document_number || n.numero || '—',
                  n.issued_at ? new Date(n.issued_at).toLocaleDateString('es-CL') : '—',
                  `$${fmtCLP(n.amount_clp)}`,
                  n.motivo || n.reason || '—',
                ])}
              />
            </div>
            <div>
              <p className='text-xs font-semibold text-amber-700 mb-1'>Notas de Débito ({detalle.notas_debito?.length || 0})</p>
              <TablaSimple
                headers={['N°', 'Fecha', 'Monto', 'Motivo']}
                rows={detalle.notas_debito?.map(n => [
                  n.document_number || n.numero || '—',
                  n.issued_at ? new Date(n.issued_at).toLocaleDateString('es-CL') : '—',
                  `$${fmtCLP(n.amount_clp)}`,
                  n.motivo || n.reason || '—',
                ])}
              />
            </div>
          </div>
        )}
        {seccion === 'gestiones' && (
          <TablaSimple
            headers={['Fecha', 'Tipo', 'Canal', 'Resultado', 'Descripción']}
            rows={detalle.gestiones?.map(g => [
              g.created_at ? new Date(g.created_at).toLocaleDateString('es-CL') : '—',
              g.tipo_gestion || g.tipo || '—',
              g.canal || '—',
              g.resultado || '—',
              (g.resumen || g.descripcion || '').slice(0, 80),
            ])}
          />
        )}
        {seccion === 'compromisos' && (
          <TablaSimple
            headers={['Fecha compromiso', 'Monto', 'Estado', 'Descripción']}
            rows={detalle.compromisos?.map(c => [
              c.fecha_prometida ? new Date(c.fecha_prometida).toLocaleDateString('es-CL') : '—',
              `$${fmtCLP(c.monto_comprometido || c.monto)}`,
              c.estado || '—',
              (c.descripcion || '').slice(0, 80),
            ])}
          />
        )}
        {seccion === 'alertas' && (
          <TablaSimple
            headers={['Fecha', 'Tipo', 'Prioridad', 'Estado', 'Mensaje']}
            rows={detalle.alertas?.map(a => [
              a.created_at ? new Date(a.created_at).toLocaleDateString('es-CL') : '—',
              a.tipo || '—',
              a.prioridad || '—',
              a.estado || '—',
              (a.mensaje || '').slice(0, 80),
            ])}
          />
        )}
        {seccion === 'comunicaciones' && (
          <div className='space-y-3'>
            {(detalle.comunicaciones || []).length === 0 ? (
              <p className='text-xs text-slate-400 text-center py-4'>Sin comunicaciones registradas</p>
            ) : (
              detalle.comunicaciones.map((c) => (
                <div key={c.id} className='border border-slate-200 rounded p-2 bg-white' data-testid={`comunicacion-item-${c.id}`}>
                  <div className='flex items-center justify-between text-xs'>
                    <div>
                      <p className='font-semibold text-slate-900'>{c.tipo || '—'} · {c.asunto || '(sin asunto)'}</p>
                      <p className='text-slate-500'>
                        Última acción: {c.email_destino || '—'} · Estado:
                        <span className={`ml-1 font-semibold ${c.estado === 'no_enviado' ? 'text-red-700' : 'text-green-700'}`}>
                          {c.estado || '—'}
                        </span>
                        {c.error_envio && <span className='ml-2 text-red-600'>· {c.error_envio}</span>}
                      </p>
                      <p className='text-[10px] text-slate-400'>Intentos: {c.intentos ?? 1} · Enviado por {c.enviado_por || '—'} · {c.created_at ? new Date(c.created_at).toLocaleString('es-CL') : '—'}</p>
                    </div>
                  </div>
                  {(c.intentos_historial && c.intentos_historial.length > 0) && (
                    <details className='mt-2' data-testid={`historial-toggle-${c.id}`}>
                      <summary className='text-[11px] text-blue-700 cursor-pointer'>Ver historial completo de {c.intentos_historial.length} intento(s)</summary>
                      <div className='mt-2 space-y-2 pl-2 border-l-2 border-slate-200'>
                        {c.intentos_historial.map((h, idx) => (
                          <div key={idx} className={`text-[11px] p-2 rounded ${h.estado === 'no_enviado' ? 'bg-red-50' : 'bg-green-50'}`}>
                            <p className='font-semibold'>#{h.numero_intento} · {h.origen} · {h.estado}</p>
                            <p>Destinatario: <span className='font-mono'>{h.email_destino}</span></p>
                            {h.contacto_usado && <p>Contacto: {h.contacto_usado.nombre} ({h.contacto_usado.rol || '—'})</p>}
                            <p>Usuario: {h.usuario_nombre} · {h.fecha_hora ? new Date(h.fecha_hora).toLocaleString('es-CL') : '—'}</p>
                            <p>Asunto: {h.asunto}</p>
                            {(h.adjuntos || []).length > 0 && (
                              <p>Adjuntos: {h.adjuntos.map(a => a.nombre || a.url).join(', ')}</p>
                            )}
                            {h.error_envio && <p className='text-red-700 mt-1'>Error: <code>{h.error_envio}</code></p>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))
            )}
          </div>
        )}
        {seccion === 'contactos' && (
          <TablaSimple
            headers={['Origen', 'Nombre', 'Email', 'Teléfono', 'Rol']}
            rows={detalle.contactos?.map(c => [
              c.origen || '—',
              c.nombre || '—',
              c.email || '—',
              c.telefono || '—',
              c.rol || '—',
            ])}
          />
        )}
      </div>
    </div>
  );
};

const TablaSimple = ({ headers, rows }) => {
  if (!rows || rows.length === 0) {
    return <p className='text-xs text-slate-400 text-center py-4'>Sin registros</p>;
  }
  return (
    <table className='w-full text-xs'>
      <thead className='bg-slate-100'>
        <tr>
          {headers.map((h, i) => <th key={i} className='p-2 text-left font-semibold text-slate-600'>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className='border-b border-slate-100'>
            {r.map((c, j) => <td key={j} className='p-2 text-slate-700 align-top'>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ---------------------- Vista por tramos ----------------------
const VistaPorTramos = ({
  data, loading, tramoActivo, tramoResumen, onChangeTramo, page, setPage,
  onAbrirRegistrarPago, onAbrirNotaCredito, onAbrirNotaDebito,
}) => {
  const facturas = data.facturas || [];
  const totalPages = data.total_pages || 1;

  return (
    <>
      {/* Tarjetas resumen por tramo */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
        {TRAMOS.map(t => {
          const info = tramoResumen[t.key] || { count: 0, saldo: 0 };
          const isActive = tramoActivo === t.key;
          return (
            <button
              key={t.key}
              type='button'
              onClick={() => onChangeTramo(t.key)}
              data-testid={`tramo-tab-${t.key}`}
              className={`text-left p-3 rounded-lg border-2 transition-all ${
                isActive ? `${t.bg} border-current shadow-sm ${t.color}` : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${isActive ? t.color : 'text-slate-500'}`}>{t.label}</p>
              <p className={`text-lg font-bold mt-1 ${isActive ? t.color : 'text-slate-900'}`}>${fmtCLP(info.saldo)}</p>
              <p className='text-xs text-slate-500 mt-0.5'>{info.count} facturas</p>
            </button>
          );
        })}
      </div>

      {/* Tabla del tramo seleccionado */}
      <Card className='border-slate-200'>
        <div className='overflow-x-auto max-h-[500px] overflow-y-auto'>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2' />
              <p className='text-slate-600'>Cargando facturas...</p>
            </div>
          ) : facturas.length === 0 ? (
            <div className='p-12 text-center'>
              <Receipt className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-slate-600 font-medium'>No hay facturas en este tramo</p>
            </div>
          ) : (
            <table className='w-full text-sm'>
              <thead className='bg-slate-50 sticky top-0 z-10'>
                <tr>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>N° Factura</th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>Cliente</th>
                  <th className='text-left p-3 font-semibold text-slate-600 border-b'>RUT</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Total</th>
                  <th className='text-right p-3 font-semibold text-slate-600 border-b'>Pendiente</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Vencimiento</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Días</th>
                  <th className='text-center p-3 font-semibold text-slate-600 border-b'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map(f => (
                  <tr key={f.id} className='border-b border-slate-100 hover:bg-slate-50'>
                    <td className='p-3'>
                      <p className='font-bold text-slate-900'>#{f.numero_factura}</p>
                      <p className='text-[10px] text-slate-400 font-mono'>cid: {f.client_id?.slice(0, 8)}</p>
                    </td>
                    <td className='p-3'>
                      <p className='font-medium text-slate-900'>{f.cliente?.company || f.cliente?.name}</p>
                    </td>
                    <td className='p-3 font-mono text-xs'>{f.cliente?.rut || '—'}</td>
                    <td className='p-3 text-right'>${fmtCLP(f.total_amount_clp)}</td>
                    <td className='p-3 text-right font-semibold text-red-700'>${fmtCLP(f.pending_amount_clp)}</td>
                    <td className='p-3 text-center'>
                      {f.fecha_vencimiento_dt ? new Date(f.fecha_vencimiento_dt).toLocaleDateString('es-CL') : '—'}
                    </td>
                    <td className='p-3 text-center'>
                      {f.dias_vencido > 0 ? (
                        <Badge variant='outline' className='bg-red-50 text-red-700 border-red-200'>{f.dias_vencido}</Badge>
                      ) : <span className='text-slate-400 text-xs'>vigente</span>}
                    </td>
                    <td className='p-3 text-center'>
                      <div className='flex flex-col gap-1'>
                        <Button size='sm' variant='outline' className='h-6 text-[10px]' onClick={() => onAbrirRegistrarPago?.(f)}>
                          <DollarSign className='w-3 h-3 mr-1' /> Pago
                        </Button>
                        <div className='flex gap-1'>
                          <Button size='sm' variant='outline' className='h-6 text-[10px] flex-1' onClick={() => onAbrirNotaCredito?.(f)}>NC</Button>
                          <Button size='sm' variant='outline' className='h-6 text-[10px] flex-1' onClick={() => onAbrirNotaDebito?.(f)}>ND</Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Paginacion page={page} totalPages={totalPages} onChange={setPage} total={data.total} />
    </>
  );
};

// ---------------------- Paginación ----------------------
const Paginacion = ({ page, totalPages, onChange, total }) => {
  if (!totalPages || totalPages <= 1) {
    return total !== undefined ? (
      <p className='text-xs text-slate-500 text-center'>{total} registros</p>
    ) : null;
  }
  return (
    <div className='flex items-center justify-center gap-2 py-2'>
      <Button size='sm' variant='outline' className='h-7' disabled={page <= 1}
              onClick={() => onChange(page - 1)} data-testid='pag-prev'>
        <ArrowLeft className='w-3 h-3' />
      </Button>
      <span className='text-xs text-slate-600'>Página {page} de {totalPages} · {total} registros</span>
      <Button size='sm' variant='outline' className='h-7' disabled={page >= totalPages}
              onClick={() => onChange(page + 1)} data-testid='pag-next'>
        <ArrowRight className='w-3 h-3' />
      </Button>
    </div>
  );
};

export default EnCobroConsolidado;
